import { useTranslation } from "@redotech/redo-i18n";
import type { MultipleChoiceState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import {
  BadgeRedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext, useMemo } from "react";
import { FlowOptionsContext } from "../condition";

export const MultipleChoiceDetails = memo(function MultipleChoiceDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<MultipleChoiceState>) {
  const { t } = useTranslation();
  const flowOptions = useContext(FlowOptionsContext);

  const questionOptions: TextRedoListItem<string>[] = useMemo(() => {
    const questions = flowOptions?.multipleChoiceQuestions || [];
    return questions.map((question) => ({
      type: "text" as const,
      id: question.message,
      text: question.message,
      value: question.message,
    }));
  }, [flowOptions]);

  const selectedQuestion = useMemo(() => {
    return questionOptions.find((opt) => opt.value === state.questionText);
  }, [questionOptions, state.questionText]);

  const answerOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!state.questionText || !flowOptions?.multipleChoiceQuestions) {
      return [];
    }
    const question = flowOptions.multipleChoiceQuestions.find(
      (q) => q.message === state.questionText,
    );
    if (!question) {
      return [];
    }
    return question.choices.map((choice) => ({
      type: "badge" as const,
      id: choice,
      value: choice,
      badge: { text: choice, color: "gray" as const },
    }));
  }, [flowOptions, state.questionText]);

  const selectedAnswers: BadgeRedoListItem<string>[] = useMemo(() => {
    return state.answers.map((answer) => ({
      type: "badge" as const,
      id: answer,
      value: answer,
      badge: { text: answer, color: "gray" as const },
    }));
  }, [state.answers]);

  return (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        label={t("Question")}
        options={questionOptions}
        optionSelected={({ item }) => {
          setState({ ...state, questionText: item.value, answers: [] });
        }}
        placeholder={t("Select a question...")}
        selectedItem={selectedQuestion}
        size="xs"
      />
      {state.questionText && (
        <RedoTagsDropdownInput
          disabled={disabled}
          label={t("Answers")}
          options={answerOptions}
          placeholder={t("Select answers...")}
          selectedOptions={selectedAnswers}
          setSelectedOptions={(items) => {
            setState({ ...state, answers: items.map((item) => item.value) });
          }}
          size="xs"
        />
      )}
    </Flex>
  );
});
